// src/components/digital_twin/RunoffFlow3DLayer.js
// FlowSense MOW+ — rainfall + run-off overlay for the Digital Twin (rainfall_runoff scenario)
//   • Overflow routes: downhill flow from a full well / catch basin to the next lower collector
//     (TessaDEM terrain). Width = overflow volume; animated dashes run downhill.
//   • Rain: one THREE.Points cloud over the drainage network, density and fall speed follow the
//     rainfall of the current playback hour (or the peak hour). Stops when the tab is hidden,
//     the layer is hidden, or the hour is dry — no idle render loop.
// Positioning reuses SimulationImpact3DLayer's projector so overlays line up with the scene.
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { buildProjector } from './SimulationImpact3DLayer';

const MAX_RAIN = 4000;
const MAX_FLOWS = 1500;
const DROP_HEIGHT_M = 900;

const disposeGroup = (group) => {
  group.traverse((o) => {
    if (o.geometry) o.geometry.dispose();
    if (o.material) (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => m.dispose());
  });
  while (group.children.length) group.remove(group.children[0]);
};

const RunoffFlow3DLayer = ({ scene, origin, map, assets = [], result, playbackHour = null, visible = true }) => {
  const groupRef = useRef(null);
  const stateRef = useRef({ rain: null, flows: null, bounds: null, m2s: 1, baseZ: 0, rate: 0, raf: null });

  const assetsById = useMemo(() => {
    const m = new Map();
    for (const a of assets) m.set(String(a.id), a);
    return m;
  }, [assets]);
  const assetsRef = useRef(assetsById);
  assetsRef.current = assetsById;

  const timeline = result?.timeline || null;
  const peakRate = useMemo(() => (timeline?.length ? Math.max(0, ...timeline.map((t) => t.rain_mm || 0)) : 0), [timeline]);

  // ── build geometry when the result changes ──────────────────────────────
  useEffect(() => {
    if (!scene || !result) return undefined;
    let group = groupRef.current;
    if (!group) {
      group = new THREE.Group();
      group.name = 'runoffFlow';
      group.renderOrder = 905;
      scene.add(group);
      groupRef.current = group;
    }
    disposeGroup(group);
    const proj = buildProjector(scene, origin, assetsRef.current);
    const m2s = proj.metersToScene;
    const st = stateRef.current;
    st.m2s = m2s; st.baseZ = proj.baseZ; st.flows = null; st.rain = null;

    // Overflow routes
    const flows = (result.flows || []).slice(0, MAX_FLOWS);
    if (flows.length) {
      const maxVol = Math.max(1, ...flows.map((f) => f.volume_m3 || 0));
      const pos = []; const col = [];
      const low = new THREE.Color(0x63b3ed); const high = new THREE.Color(0x2b6cb0);
      for (const f of flows) {
        const a = proj.objectById.get(String(f.from_asset_id))?.position.clone() || proj.toScene(f.from.lat, f.from.lng);
        const b = proj.objectById.get(String(f.to_asset_id))?.position.clone() || proj.toScene(f.to.lat, f.to.lng);
        if (!a || !b) continue;
        const z = 12 * m2s;
        pos.push(a.x, a.y, a.z + z, b.x, b.y, b.z + z);
        const c = low.clone().lerp(high, Math.min(1, (f.volume_m3 || 0) / maxVol));
        col.push(c.r, c.g, c.b, c.r, c.g, c.b);
      }
      if (pos.length) {
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
        g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
        const line = new THREE.LineSegments(g, new THREE.LineDashedMaterial({
          vertexColors: true, dashSize: 25 * m2s, gapSize: 15 * m2s, transparent: true, opacity: 0.95, depthWrite: false,
        }));
        line.computeLineDistances();
        line.name = 'runoff-routes';
        group.add(line);
        st.flows = line;
      }
    }

    // Rain cloud over the drainage network bounds
    const nodes = result.affectedNodes || [];
    const pts = nodes.map((n) => proj.objectById.get(String(n.asset_id))?.position || proj.toScene(Number(n.latitude), Number(n.longitude))).filter(Boolean);
    if (pts.length && peakRate > 0) {
      const box = new THREE.Box3().setFromPoints(pts);
      const pad = 300 * m2s;
      box.expandByVector(new THREE.Vector3(pad, pad, 0));
      st.bounds = box;
      const pos = new Float32Array(MAX_RAIN * 3);
      for (let i = 0; i < MAX_RAIN; i++) {
        pos[i * 3] = box.min.x + Math.random() * (box.max.x - box.min.x);
        pos[i * 3 + 1] = box.min.y + Math.random() * (box.max.y - box.min.y);
        pos[i * 3 + 2] = proj.baseZ + Math.random() * DROP_HEIGHT_M * m2s;
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      g.setDrawRange(0, 0);
      const rain = new THREE.Points(g, new THREE.PointsMaterial({ color: 0x90cdf4, size: 3, sizeAttenuation: false, transparent: true, opacity: 0.55, depthWrite: false }));
      rain.name = 'runoff-rain';
      rain.frustumCulled = false;
      group.add(rain);
      st.rain = rain;
    }
    map?.triggerRepaint?.();
    return undefined;
    // origin compared by value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, origin?.x, origin?.y, result, map, peakRate]);

  // ── rain rate for the current hour + animation loop ─────────────────────
  useEffect(() => {
    const st = stateRef.current;
    const group = groupRef.current;
    if (group) group.visible = !!visible;
    const hour = playbackHour == null ? null : Math.round(playbackHour);
    const frame = hour == null ? timeline?.reduce((b, t) => ((t.rain_mm || 0) > (b?.rain_mm || 0) ? t : b), null) : timeline?.find((t) => t.hour === hour);
    st.rate = frame?.rain_mm || 0;
    if (st.rain) st.rain.geometry.setDrawRange(0, Math.round(Math.min(1, st.rate / Math.max(10, peakRate)) * MAX_RAIN));

    const stop = () => { if (st.raf) cancelAnimationFrame(st.raf); st.raf = null; };
    stop();
    if (!visible || (!st.rain && !st.flows)) { map?.triggerRepaint?.(); return stop; }

    let last = performance.now();
    const tick = (now) => {
      st.raf = null;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const dt = Math.min(0.1, (now - last) / 1000); last = now;
      if (st.rain && st.rate > 0) {
        const p = st.rain.geometry.attributes.position;
        const n = st.rain.geometry.drawRange.count;
        const fall = (250 + st.rate * 12) * st.m2s * dt;
        const top = st.baseZ + DROP_HEIGHT_M * st.m2s;
        for (let i = 0; i < n; i++) {
          const z = p.array[i * 3 + 2] - fall;
          p.array[i * 3 + 2] = z < st.baseZ ? top : z;
        }
        p.needsUpdate = true;
      }
      if (st.flows) st.flows.material.dashOffset -= 40 * st.m2s * dt;
      map?.triggerRepaint?.();
      st.raf = requestAnimationFrame(tick);
    };
    st.raf = requestAnimationFrame(tick);
    const onVis = () => { if (document.visibilityState === 'visible' && !st.raf) { last = performance.now(); st.raf = requestAnimationFrame(tick); } };
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
  }, [playbackHour, visible, timeline, peakRate, map, result]);

  // ── cleanup ─────────────────────────────────────────────────────────────
  useEffect(() => () => {
    const st = stateRef.current;
    if (st.raf) cancelAnimationFrame(st.raf);
    const group = groupRef.current;
    if (group) { disposeGroup(group); group.parent?.remove(group); }
    groupRef.current = null;
  }, []);

  return null;
};

export default RunoffFlow3DLayer;
